const LearningObjectTypes = {
  Course: 'course',
  LearningActivity: 'learningActivity',
  PlenaryLesson: 'plenaryLesson',
  PBL: 'pbl'
} as const;

export type LearningObjectTypes = (typeof LearningObjectTypes)[keyof typeof LearningObjectTypes];

const ElementType = {
  AssignmentFeedback: 'assignmentFeedback',
  DraftNoteToTeacher: 'draftNoteToTeacher',
  DraftFeedbackForAssignment: 'draftFeedbackForAssignment',
  SatisfactionScore: 'satisfactionScore',
  SubmissionHistory: 'submissionHistory',
  CollaborativeState: 'collaborativeState',
  SummaryPageSeen: 'summaryPageSeen',
  PreviousQuestionsAnswers: 'previousQuestionsAnswers',
  ManualFeedback: 'manualFeedback',
  QuestionGroups: 'questionGroups',
  Question: 'question',
  Status: 'status'
} as const;
export type ElementType = (typeof ElementType)[keyof typeof ElementType];

const FEEDBACK_ELEMENT_ID_SUFFIX = '_feedback';

function getFeedbackElementId(questionId: string): string {
  return `${questionId}${FEEDBACK_ELEMENT_ID_SUFFIX}`;
}

function getQuestionIdFromFeedbackElementId(feedbackId: string): string {
  return feedbackId.replace(new RegExp(`${FEEDBACK_ELEMENT_ID_SUFFIX}$`), '');
}

function isFeedbackElementId(id: string): boolean {
  return id.endsWith(FEEDBACK_ELEMENT_ID_SUFFIX);
}

const FeedbackMessages = {
  NoAnswer: 'noAnswer',
  OnlyWrongAnswers: 'onlyWrongAnswers',
  NotAllCorrectAndNoWrongAnswers: 'notAllCorrectAndNoWrongAnswers',
  SomeCorrectAndSomeWrongAnswers: 'someCorrectAndSomeWrongAnswers',
  OnlyAllCorrectAnswers: 'onlyAllCorrectAnswers',
  ShowAnswer: 'showAnswer',
  NoAnswerButNotRequired: 'noAnswerButNotRequired'
} as const;
export type FeedbackMessages = (typeof FeedbackMessages)[keyof typeof FeedbackMessages];

const CourseEntryMessageType = {
  NoEnvironments: 'noEnvironments',
  NoClassesAndGroups: 'noClassesAndGroups'
} as const;
export type CourseEntryMessageType = (typeof CourseEntryMessageType)[keyof typeof CourseEntryMessageType];

const StateStatus = {
  Unsubmitted: 'Unsubmitted',
  Submitted: 'Submitted',
  ReturnedToFix: 'ReturnedToFix',
  Evaluated: 'Evaluated'
} as const;
export type StateStatus = (typeof StateStatus)[keyof typeof StateStatus];

const TaskStatusEnum = {
  ToDo: 'todo',
  Done: 'done',
  Late: 'late',
  ReturnedForCorrection: 'returnedForCorrection',
  TillTomorrow: 'tillTomorrow',
  Checked: 'checked'
} as const;
export type TaskStatusEnum = (typeof TaskStatusEnum)[keyof typeof TaskStatusEnum];

const DashboardTaskType = {
  SelfLearning: 'selfLearning',
  LearningWork: 'learningWork',
  EvalWork: 'evalWork',
  Exam: 'exam'
} as const;
export type DashboardTaskType = (typeof DashboardTaskType)[keyof typeof DashboardTaskType];

const TaskStatus = {
  Started: 'started',
  Submitted: 'submitted',
  Assessed: 'assessed'
} as const;
export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

const PlenaryStatus = {
  Started: 'Started',
  Stopped: 'Stopped',
  ReadOnly: 'ReadOnly',
  New: 'New'
} as const;
export type PlenaryStatus = (typeof PlenaryStatus)[keyof typeof PlenaryStatus];

const AssignmentType = {
  nonGraded: 'nonGraded',
  graded: 'graded',
  exam: 'exam',
  selfLearning: 'selfLearning'
} as const;
export type AssignmentType = (typeof AssignmentType)[keyof typeof AssignmentType];

const AssignmentFeedbackTrigger = {
  studentSubmit: 'student-submit',
  teacherSubmit: 'teacher-submit',
  date: 'date'
} as const;
export type AssignmentFeedbackTrigger = (typeof AssignmentFeedbackTrigger)[keyof typeof AssignmentFeedbackTrigger];

export {
  AssignmentFeedbackTrigger,
  AssignmentType,
  CourseEntryMessageType,
  DashboardTaskType,
  ElementType,
  FEEDBACK_ELEMENT_ID_SUFFIX,
  FeedbackMessages,
  getFeedbackElementId,
  getQuestionIdFromFeedbackElementId,
  isFeedbackElementId,
  LearningObjectTypes,
  PlenaryStatus,
  StateStatus,
  TaskStatus,
  TaskStatusEnum
};
